import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { CompactIdeaResponse, IdeaResponse, IdeaId } from '@business/entities/idea';
import { IdeaAPI } from '@business/api/idea';
import { IdeasFormData } from '@modules/ideas/IdeasPage/IdeasPage';

export class IdeaBloc extends BaseBloc {
  private readonly ideaRepo = new Repository<CompactIdeaResponse | IdeaResponse>({
    getItemId: idea => idea.id,
  });

  readonly ideas$ = this.ideaRepo.items$;
  readonly selectIdea = this.ideaRepo.selectItem;

  onReset() {
    this.ideaRepo.reset();
  }

  fetchIdeas = (data?: IdeasFormData) => {
    return IdeaAPI.fetchIdeas(data).pipe(this.ideaRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_ideas_error') }));
  };

  fetchIdea = (id: IdeaId) => {
    return IdeaAPI.fetchIdea(id).pipe(
      this.ideaRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_ideas_error') }),
    );
  };

  createIdea = (idea: Partial<CompactIdeaResponse>) => {
    return IdeaAPI.createIdea(idea).pipe(
      this.ideaRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_idea'),
      }),
    );
  };

  updateIdea = (id: IdeaId, data: Partial<CompactIdeaResponse>) => {
    return IdeaAPI.updateIdea(id, data).pipe(
      this.ideaRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_idea'),
      }),
    );
  };

  deleteIdea = (id: IdeaId) => {
    return showDeleteConfirmation('Delete Idea', 'Do you really want to remove this idea?').pipe(
      switchMap(() => IdeaAPI.deleteIdea(id)),
      this.ideaRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_idea'),
      }),
    );
  };

  /* Sections */
  createComment = (ideaId: IdeaId, comment: string) => {
    return IdeaAPI.createComment(ideaId, { comment }).pipe(
      switchMap(() => this.fetchIdea(ideaId)),
      handleMessage({
        type: requestMessage('create_idea_comment'),
      }),
    );
  };

  updateComment = (ideaId: IdeaId, id: string, comment: string) => {
    return IdeaAPI.updateComment(id, { comment }).pipe(
      switchMap(() => this.fetchIdea(ideaId)),
      handleMessage({
        type: requestMessage('update_idea_comment'),
      }),
    );
  };

  deleteComment = (ideaId: IdeaId, id: string) => {
    return showDeleteConfirmation('Delete Comment', 'Do you really want to remove this Comment?').pipe(
      switchMap(() => IdeaAPI.deleteComment(id)),
      switchMap(() => this.fetchIdea(ideaId)),
      handleMessage({
        type: requestMessage('delete_idea_comment'),
      }),
    );
  };

  postVote = (ideaId: IdeaId, detailPage?: boolean) => {
    return IdeaAPI.postVote(ideaId, detailPage).pipe(
      this.ideaRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('idea_vote'),
      }),
    );
  };

  fetchCategories = () => {
    return IdeaAPI.fetchCategories().pipe(handleMessage({ error: requestMessage('fetch_ideas_error') }));
  };
}
